import React, { useRef, useState, useEffect, useMemo } from 'react';
import Quill from 'quill';
import ReactQuill from 'react-quill';

import 'react-quill/dist/quill.snow.css';
import Delta from 'quill-delta';
import { marked } from 'marked';

import { IPromptCard, ITextBlock } from '@homeporter/types';

const Parchment = Quill.import('parchment');
export const DataId = new Parchment.Attributor.Attribute(
  'dataId', // Quill format key (used in Delta attributes)
  'data-id', // DOM attribute name
  { scope: Parchment.Scope.BLOCK },
);

Quill.register(DataId, true);

export const RefAttr = new Parchment.Attributor.Attribute('ref', 'data-ref', {
  scope: Parchment.Scope.BLOCK,
});

Quill.register(RefAttr, true);

const formats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'link',
  'align',
  'size',
  'dataId',
  'ref',
];

interface QuillEditorProps {
  quillRef: React.RefObject<any>;
  card: ITextBlock | IPromptCard | null;
  setSaveEnabled: (state: boolean) => void;
}

const QuillEditor: React.FC<QuillEditorProps> = ({
  quillRef,
  card,
  setSaveEnabled,
}) => {
  const [quillInstance, setQuillInstance] = useState<any | null>(null);
  const isProgrammaticChange = useRef(false);
  const isUserUpdated = useRef(false);

  const handleEditorChange = (content: string) => {
    if (!card) return;

    if (isProgrammaticChange.current) {
      isProgrammaticChange.current = false;
      return;
    }

    setSaveEnabled(true);
    isUserUpdated.current = true;
  };

  useEffect(() => {
    if (!quillRef.current) return;

    const instance = quillRef.current.getEditor();
    setQuillInstance(instance);

    return () => {
      setQuillInstance(null);
    };
  }, [quillRef]);

  useEffect(() => {
    if (!quillInstance || !card) return;

    isProgrammaticChange.current = true;
    if (card.modified) {
      quillInstance.setContents(
        typeof card.modified === 'string'
          ? new Delta().insert(card.modified)
          : card.modified,
      );
    } else {
      const delta = quillInstance.clipboard.convert(
        marked(card.response) || '',
      );

      quillInstance.setContents(delta);
    }

    const historyModule = quillInstance.getModule('history');
    historyModule.clear();

    historyModule.stack.undo = [...card.undo];
    historyModule.stack.redo = [...card.redo];

    return () => {
      if (!quillInstance || !card || !isUserUpdated.current) return;

      const delta = quillInstance.getContents();

      card.modified =
        delta.ops.length === 1 && delta.ops[0].insert === '\n'
          ? ''
          : quillInstance.getContents();

      const historyModule = quillInstance.getModule('history');
      card.undo = [...historyModule.stack.undo];
      card.redo = [...historyModule.stack.redo];
    };
  }, [quillInstance, card?.response]);

  useEffect(() => {
    console.log(':::::new quillInstance....');
  }, [quillInstance]);

  useEffect(() => {
    if (!quillRef?.current?.getEditor()) return;

    setQuillInstance(quillRef.current.getEditor());
  }, [quillRef]);

  const modules = useMemo(
    () => ({
      toolbar: false,
      history: { delay: 1000, maxStack: 50, userOnly: true },
      keyboard: {
        bindings: {
          clearRefOnEnter: {
            key: 13, // or 'Enter'
            collapsed: true,
            handler: function (range: any /*, context: any */) {
              setTimeout(() => {
                const quill = quillRef.current?.getEditor();
                if (!quill) return;

                const sel = quill.getSelection();
                const idx = sel ? sel.index : range.index + 1;

                const [line] = quill.getLine(idx);
                if (!line) return;

                line.format('ref', false);
              }, 0);

              return true;
            },
          },
        },
      },
    }),
    [quillRef],
  );

  return (
    <ReactQuill
      ref={quillRef}
      onChange={handleEditorChange}
      formats={formats}
      modules={modules}
      theme="snow"
    />
  );
};

export default QuillEditor;
